export type MiniLoginStatus = 'PENDING' | 'SCANNED' | 'CONFIRMED' | 'CONSUMED' | 'CANCELLED' | 'EXPIRED';
export type MiniLoginMode = 'code' | 'link';

export interface MiniLoginSession {
  sessionId: string;
  expiresAt: number;
  status: MiniLoginStatus;
  qrCodeDataUrl?: string;
  miniProgramUrlLink?: string;
}

export interface PendingMiniLogin extends MiniLoginSession {
  mode: MiniLoginMode;
  returnTo: string;
}

const PREFIX = '/login/wx/web';
const STORAGE_KEY = 'imgcloud_pending_mini_login';

export class MiniLoginError extends Error {
  constructor(public code: string) { super(code); }
}

async function request<T>(path: string, body?: unknown, keepalive = false): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), path === '/session' ? 45000 : 10000);
  try {
    const response = await fetch(PREFIX + path, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Web-Login-Origin': window.location.origin },
      credentials: 'same-origin', cache: 'no-store', keepalive, signal: controller.signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok || result.isSuccess !== true) throw new MiniLoginError(result.code || 'unavailable');
    return result as T;
  } finally { window.clearTimeout(timer); }
}

export const miniLoginConfig = () => request<{ enabled: boolean }>('/config');
export const createMiniLogin = (mode: MiniLoginMode) => request<MiniLoginSession>('/session', { mode, agreementAccepted: true });
export const readMiniLogin = (id: string) => request<MiniLoginSession>(`/session/${encodeURIComponent(id)}`);
export const consumeMiniLogin = (id: string) => request<MiniLoginSession>(`/session/${encodeURIComponent(id)}/consume`, {});
export const cancelMiniLogin = (id: string) => request<MiniLoginSession>(`/session/${encodeURIComponent(id)}/cancel`, {}, true);

export function savePendingMiniLogin(session: PendingMiniLogin) {
  // Only request metadata is persisted; browser and login credentials stay in HttpOnly cookies.
  const { sessionId, expiresAt, mode, returnTo, miniProgramUrlLink } = session;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, expiresAt, mode, returnTo, miniProgramUrlLink }));
}

export function clearPendingMiniLogin() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* Storage may be disabled. */ }
}

export function restorePendingMiniLogin(): PendingMiniLogin | null {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const data = JSON.parse(saved);
    if (!/^[A-Za-z0-9_-]{22}$/.test(data.sessionId) || !Number.isFinite(data.expiresAt)
      || data.expiresAt + 60000 <= Date.now() || !['code', 'link'].includes(data.mode)
      || typeof data.returnTo !== 'string' || !data.returnTo.startsWith('/') || data.returnTo.startsWith('//')) {
      clearPendingMiniLogin();
      return null;
    }
    return { ...data, status: 'PENDING' };
  } catch {
    clearPendingMiniLogin();
    return null;
  }
}

export function openMiniProgram(link: string) {
  let url: URL;
  try {
    if (!/^https:\/\//i.test(link) || /[\s\\]/.test(link)) throw new Error();
    url = new URL(link);
    if (url.protocol !== 'https:' || !['wxaurl.cn', 'wxmpurl.cn'].includes(url.hostname)
      || url.username || url.password || url.port || url.pathname.length <= 1 || url.hash) throw new Error();
  } catch {
    throw new MiniLoginError('entryUnavailable');
  }
  window.location.assign(url.href);
}
